import React, { useState, useEffect } from 'react';
import { Skeleton, Divider, Row, Col } from 'antd';
import { motion } from 'framer-motion';
import { FileExcelOutlined, BarChartOutlined } from '@ant-design/icons';
import SODOfficerReportCard from './officerReport';
import ExceptionalReportCard from './exceptionalReport';
import axios from '../../../helpers/Axios';
import PendingIcon from '../../../assets/files/img/pending.png';
import ActivatedIcon from '../../../assets/files/img/activated.png';
import VerifiedIcon from '../../../assets/files/img/verified2.png';
import ArchivedIcon from '../../../assets/files/img/archived.png';

const BRAND = '#0f8269';

const authHeaders = {
  headers: {
    'X-Gravitee-Api-Key': process.env.REACT_APP_GRAVITEE_API_KEY_AUTHENTICATION,
  },
};

const STATS = [
  { key: 'pending', label: 'Pending', color: '#f59e0b', bg: '#fff8f0', icon: PendingIcon, status: 1 },
  { key: 'activated', label: 'Activated', color: '#3b82f6', bg: '#eff6ff', icon: ActivatedIcon, status: 3 },
  { key: 'verified', label: 'Verified', color: '#0f8269', bg: '#f0faf7', icon: VerifiedIcon, status: 4 },
  { key: 'archived', label: 'Archived', color: '#9ca3af', bg: '#f9fafb', icon: ArchivedIcon, status: 6 },
];

const StatCard = ({ label, color, bg, icon, value, loading, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25, delay: index * 0.06 }}
    style={{
      background: '#fff',
      borderRadius: 12,
      border: '1px solid #f0f0f0',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      padding: '18px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 14,
      height: '100%',
    }}
  >
    <div>
      {loading ? (
        <Skeleton.Input active size="small" style={{ width: 60, height: 32, borderRadius: 6 }} />
      ) : (
        <p style={{ margin: 0, fontSize: 30, fontWeight: 800, color, lineHeight: 1, letterSpacing: '-0.03em' }}>
          {value.toLocaleString()}
        </p>
      )}
      <p style={{
        margin: '6px 0 0', fontSize: 11, fontWeight: 700,
        color: loading ? '#d1d5db' : '#6b7280',
        textTransform: 'uppercase', letterSpacing: '0.07em',
      }}>
        {label}
      </p>
    </div>
    <div style={{
      width: 48, height: 48, borderRadius: 12, background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <img src={icon} alt={label} style={{ width: 28, height: 28, opacity: 0.9 }} />
    </div>
  </motion.div>
);

const ReportCard = ({ title, description, icon, children }) => (
  <div style={{
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #f0f0f0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  }}>
    {/* Card header */}
    <div style={{
      padding: '16px 20px',
      background: '#fafafa',
      borderBottom: '1px solid #f0f0f0',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 8,
        background: '#e6f4f0', color: BRAND,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 15, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827' }}>{title}</p>
        <p style={{ margin: 0, fontSize: 12, color: '#9ca3af', lineHeight: 1.4 }}>{description}</p>
      </div>
    </div>

    {/* Form body */}
    <div style={{ padding: '20px 20px 20px', flex: 1 }}>
      {children}
    </div>
  </div>
);

const SODDashboard = React.memo(() => {
  const storedData = JSON.parse(localStorage.getItem('user_data') || '{}');
  const firstName = storedData?.first_name || storedData?.username || 'SOD Officer';
  const today = new Date().toLocaleDateString('en-PH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const [counts, setCounts] = useState({ pending: 0, activated: 0, verified: 0, archived: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled(
          // STATS.map((s) => axios.get(`/api/company/status_count/?status=${s.status}`))
          STATS.map((s) => axios.get(`https://api.fortisph.space/gateway/authentication/companies/status/?status=${s.status}`, authHeaders))
        );
        setCounts({
          pending: results[0].status === 'fulfilled' ? (results[0].value.data.count ?? 0) : 0,
          activated: results[1].status === 'fulfilled' ? (results[1].value.data.count ?? 0) : 0,
          verified: results[2].status === 'fulfilled' ? (results[2].value.data.count ?? 0) : 0,
          archived: results[3].status === 'fulfilled' ? (results[3].value.data.count ?? 0) : 0,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="p-6" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Welcome ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <p style={{ margin: 0, fontSize: 13, color: '#9ca3af', fontWeight: 500 }}>{today}</p>
          <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 800, color: '#111827' }}>
            Welcome back, <span style={{ color: BRAND }}>{firstName}</span>
          </h1>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: '#e6f4f0', color: BRAND,
          padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
        }}>
          <BarChartOutlined />
          SOD Officer Dashboard
        </div>
      </div>

      <Divider style={{ margin: 0, borderColor: '#e5e7eb' }} />

      {/* ── Stats ── */}
      <Row gutter={[14, 14]}>
        {STATS.map(({ key, label, color, bg, icon }, i) => (
          <Col xs={24} sm={12} lg={6} key={key}>
            <StatCard
              label={label}
              color={color}
              bg={bg}
              icon={icon}
              value={counts[key]}
              loading={loading}
              index={i}
            />
          </Col>
        ))}
      </Row>

      {/* ── Reports ── */}
      <Row gutter={[20, 20]}>
        <Col xs={24} lg={12}>
          <ReportCard
            title="Officer Report"
            description="Filter by officer, status, or action — exports as Excel"
            icon={<FileExcelOutlined />}
          >
            <SODOfficerReportCard />
          </ReportCard>
        </Col>
        <Col xs={24} lg={12}>
          <ReportCard
            title="Exceptional Enrolment Report"
            description="Export companies with exceptional enrolment within a date range"
            icon={<FileExcelOutlined />}
          >
            <ExceptionalReportCard />
          </ReportCard>
        </Col>
      </Row>

    </div>
  );
});

export default SODDashboard;
